<template>
  <main
    class="search-main"
    :class="{
      'show-map': $isDesktop && searchStore.showMap,
      desktop: $isDesktop,
    }"
    data-new-results-scroll-target
  >
    <div
      v-observe-visibility="{
        callback: headerVisibilityChanged,
        throttle: 100,
        throttleOptions: {
          leading: 'hidden',
        },
      }"
    />
    <client-only>
      <favourites-onboarding
        v-if="onboardingStore.shouldShowFavouritesOnboarding"
      />
    </client-only>
    <search-breadcrumbs
      v-if="
        $isDesktop &&
        searchStore.context.results &&
        searchStore.context.results.breadcrumbs
      "
      :breadcrumbs="searchStore.context.results.breadcrumbs"
      :counts="searchStore.context.results.facetHierarchy"
      :with-counts="true"
      class="search-breadcrumbs"
    />
    <section class="search-results-section">
      <div class="search-results-column">
        <template v-if="$isDesktop">
          <div class="search-bar-desktop">
            <search-bar-desktop
              :key="`search-bar-${$route.name}`"
              :is-search-results="true"
            />
            <search-summary-bar-filters
              class="header-wrapper"
              :search-filters="searchStore.searchFilters"
              @show-map="showMapEvent($event)"
              :class="[{ pinned: !headerIsVisible }]"
            />
          </div>
        </template>

        <slot name="before-summary" />

        <search-summary-main
          v-bind="summaryProps"
          :count="count"
          :placename="placename"
          :query="query"
          :search-filters="searchStore.searchFilters"
          :sort-option="sortOption"
        >
          <template #search-summary-heading>
            <slot name="search-summary-heading">
              <h1
                v-if="heading && showDefaultHeading"
                v-thtml="heading"
                data-test="search-summary-hierarchy"
            /></slot>
          </template>
        </search-summary-main>

        <search-results-list
          :search-filters="searchStore.searchFilters"
          :pagination-data="paginationData"
          :campsites="campsites"
          :is-point-search="isIntentionalPointSearch"
          @scrolled-results-down="scrolledResultsDown"
          @visible-campsite-change="onVisibleCampsiteChange($event)"
        >
          <template #pagination-controls>
            <slot
              name="pagination-controls"
              :pagination-data="paginationData"
              :search-filters="searchStore.searchFilters"
            >
              <lazy-search-results-pagination-controls
                :pagination-data="paginationData"
                :search-filters="searchStore.searchFilters"
                :use-current-route="useSeoPagination"
              />
            </slot>
          </template>
        </search-results-list>

        <section v-if="!$isDesktop" ref="headerSection" class="header-wrapper">
          <div
            :class="[
              {
                pinned: !headerIsVisible,
                down: scrolledDown,
              },
              'showing-summary',
            ]"
          >
            <search-nav-bar
              :search-filters="searchStore.searchFilters"
              :visible-marker="visibleCampsite"
              :results-count="count"
              class="navbar"
            />

            <search-summary-bar
              :search-filters="searchStore.searchFilters"
              class="summary-bar"
            />
          </div>
        </section>

        <search-engagement-narrow-links
          v-if="!isCityLevel && !searchStore.searchFilters?.dates"
          :hierarchy-path="
            searchStore.searchFilters?.hierarchyPath || undefined
          "
          :category-ids="searchStore.searchFilters?.categoryIds || []"
          :facets="searchStore.searchFilters?.facets || undefined"
          :party="searchStore.searchFilters?.party || undefined"
          :show-counts="
            !hierarchyTrail || (hierarchyTrail && hierarchyTrail.length < 3)
          "
        />
        <slot name="search-guides" />

        <template v-if="facetMetaData && facetMetaData.og_image">
          <img
            loading="lazy"
            :src="`/_cfi/cdn-cgi/image/format=auto,fit=cover,quality=40,w=720,h=540${facetMetaData.og_image}`"
            :alt="facetMetaData.name"
            class="facet-search-image"
          />
        </template>

        <slot name="search-pagepart">
          <search-results-description
            v-if="pagePart"
            :class="{ desktop: $isDesktop }"
            :page-part="pagePart"
          />
        </slot>

        <lazy-search-results-engagement-facets
          v-if="
            searchStore.context.results &&
            searchStore.context.results.engagementFacets &&
            searchStore.context.results.engagementFacets.items.length
          "
          :items="searchStore.context.results.engagementFacets.items"
          :title="searchStore.context.results.engagementFacets.title"
        />
        <lazy-notify-me
          v-if="shouldShowNotifyMe"
          :is-search-result-alert="true"
          :search-filters="searchStore.searchFilters"
        />

        <!-- DEFENSIVE: Guard async component with mount check to prevent DOM operations before hydration -->
        <AsyncSearchLowResult
          v-if="shouldShowLowResultsPanel && isMountedRef"
          :key="getAsyncComponentKey('searchLowResult')"
          :search-filters="searchStore.searchFilters"
          :hierarchy-trail="hierarchyTrail"
          :city-point="cityPoint"
          @vue:mounted="setAsyncComponentKey('searchLowResult')"
        />

        <slot name="popular-locations">
          <lazy-search-results-popular-locations />
        </slot>
        <slot name="lower-pagepart" />

        <lazy-search-engagement-destinations
          v-if="
            searchStore.context.results &&
            searchStore.context.results.engagementDestinations &&
            searchStore.context.results.engagementDestinations.items &&
            searchStore.context.results.engagementDestinations.items.length
          "
          :title="searchStore.context.results.engagementDestinations.title"
          :items="searchStore.context.results.engagementDestinations.items"
        />

        <lazy-search-results-faq
          v-if="faqList && faqList.length"
          :faq-list="faqList"
        />
      </div>
      <section v-if="$isDesktop && searchStore.showMap" class="map-placeholder">
        <!-- DEFENSIVE: Only render map after component is mounted to prevent insertBefore errors during SSR/hydration -->
        <AsyncMapsPage
          v-if="isMountedRef"
          :key="getAsyncComponentKey('mapsPage')"
          :hovered-campsite="visibleCampsite?.slug"
          @marker-clicked="debouncedMapMarkerClicked"
          @vue:mounted="setAsyncComponentKey('mapsPage')"
        />
      </section>
    </section>

    <search-breadcrumbs
      v-if="
        searchStore.context.results && searchStore.context.results.breadcrumbs
      "
      :breadcrumbs="searchStore.context.results.breadcrumbs"
      :counts="searchStore.context.results.facetHierarchy"
      :with-counts="true"
      :show-schema="true"
      class="search-breadcrumbs"
    />
    <secondary-breadcrumbs
      v-if="
        searchStore.context.results && searchStore.context.results.breadcrumbs
      "
      :breadcrumbs="searchStore.context.results.breadcrumbs"
      :counts="searchStore.context.results.facetHierarchy"
      :show-schema="true"
    />
    <!-- DEFENSIVE: Guard async component with mount check to prevent DOM operations before hydration -->
    <AsyncBrowseAllCategories
      v-if="isMountedRef"
      :key="getAsyncComponentKey('browseAllCategories')"
      :current-hierarchy="currenthierarchyItem"
      :selected-category-ids="selectedCategoryIds"
      @vue:mounted="setAsyncComponentKey('browseAllCategories')"
    />
    <client-only>
      <lazy-back-to-top />
    </client-only>
    <nuxt-to-meta :nuxt2meta="head" />
  </main>
</template>

<script setup lang="ts">
import { debounce } from 'lodash-es'
import { useNuxtApp, useRoute, useRuntimeConfig } from 'nuxt/app'
import { puCurrency } from '~/lang/formatters/puCurrency'
import {
  computed,
  defineAsyncComponent,
  hydrateOnIdle,
  nextTick,
  onBeforeUnmount,
  onMounted,
  Ref,
  ref,
  watch,
} from 'vue'
import { useSearchStoreSetup } from '~/stores/useSearchStore'
import { transformImageSrcSet } from '~/utility/image-url'
import { PaginationData } from '~/utility/pagination'
import SCHEMA_ORGANIZATION from '../../../json/schemaOrganization.json'
import { hydrateOnVisibleSafe } from '../../../utility/hydration/hydrateOnVisibleSafe'
import { logger } from '../../../utility/logger'
import { isPointSearch } from '../domain/isPointSearch'
import { HierarchyTrail, SimpleGeoPoint } from '../schemas'

// Debug logging helper for async component lifecycle
// Logs in production to create Sentry breadcrumbs for debugging insertBefore errors
const logAsyncComponentLifecycle = (
  componentName: string,
  event: string,
  details?: any,
) => {
  logger(
    `[SearchMain:${componentName}] ${event}`,
    details
      ? { ...details, timestamp: performance.now().toFixed(2) }
      : { timestamp: performance.now().toFixed(2) },
  )
}

// Lazy components with hydration strategies
const AsyncMapsPage = defineAsyncComponent({
  loader: async () => {
    logAsyncComponentLifecycle('AsyncMapsPage', 'Loading started')
    const component = await import('../../maps-search/MapsPage.vue')
    logAsyncComponentLifecycle('AsyncMapsPage', 'Loading completed')
    return component
  },
  hydrate: hydrateOnIdle(10000),
})

const AsyncBrowseAllCategories = defineAsyncComponent({
  loader: async () => {
    logAsyncComponentLifecycle('AsyncBrowseAllCategories', 'Loading started')
    const component = await import(
      '../../categories/components/BrowseAllCategories.vue'
    )
    logAsyncComponentLifecycle('AsyncBrowseAllCategories', 'Loading completed')
    return component
  },
  hydrate: hydrateOnVisibleSafe(),
})

const AsyncSearchLowResult = defineAsyncComponent({
  loader: async () => {
    logAsyncComponentLifecycle('AsyncSearchLowResult', 'Loading started')
    const component = await import('./low-results/SearchLowResult.vue')
    logAsyncComponentLifecycle('AsyncSearchLowResult', 'Loading completed')
    return component
  },
  hydrate: hydrateOnVisibleSafe(),
})

const { $puScrollTo, $gtm, $isDesktop, $i18n, $t } = useNuxtApp()
const route = useRoute()

withDefaults(
  defineProps<{
    query?: string
    placename?: string
    selectedCategoryIds?: Array<string>
    showDefaultHeading?: boolean
  }>(),
  {
    query: undefined,
    placename: undefined,
    selectedCategoryIds: undefined,
    showDefaultHeading: true,
  },
)

const searchStore = useSearchStoreSetup(usePinia())
const onboardingStore = useOnboardingStore(usePinia())
// Removed redirectIfInvalid - validation should only happen when removing facet lozenges

// Track mount state to prevent DOM updates before component is mounted
const isMountedRef = ref(false)

onMounted(() => {
  isMountedRef.value = true
})

onBeforeUnmount(() => {
  logAsyncComponentLifecycle('SearchMain', 'Component unmounting', {
    route: route.fullPath,
  })
  // DEFENSIVE: Set unmounting flag immediately to stop all DOM operations
  // This prevents insertBefore errors during async component cleanup
  isMountedRef.value = false
})

// Function to push GTM data for search results

function pushGtmSearchResultsData(currentCampsites: any[] | undefined) {
  // Guard: Prevent execution if component is unmounting
  if (!isMountedRef.value) {
    return
  }

  if ($gtm && currentCampsites && currentCampsites.length > 0) {
    // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment -- campsites from search results
    const campsiteIds: string[] = currentCampsites
      // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access -- campsite from search results
      .filter((campsite) => campsite.bookable === true)
      // eslint-disable-next-line @typescript-eslint/no-unsafe-return, @typescript-eslint/no-unsafe-member-access -- campsite from search results
      .map((campsite) => campsite.id)
      .filter((id) => id !== undefined && id !== null)
    if (campsiteIds.length > 0) {
      const searchResultsData: {
        content_ids: string[]
        country?: string
        area?: string
        region?: string
        city?: string
        checkin_date?: string
        checkout_date?: string
        num_adults?: number
        num_children?: number
      } = {
        content_ids: campsiteIds,
      }

      const hierarchyTrail = searchStore.context.results?.hierarchyTrail
      if (hierarchyTrail && hierarchyTrail.length > 0) {
        // Map hierarchy levels based on slash count in path
        // Level 0 (0 slashes) = country, Level 1 (2 slashes) = area,
        // Level 2 (3 slashes) = region, Level 3 (4 slashes) = city
        hierarchyTrail.forEach((item) => {
          if (!item.path || !item.name) return

          const slashCount = (item.path.match(/\//g) || []).length

          switch (slashCount) {
            case 0:
            case 1: // Country level (e.g., "Scotland/")
              searchResultsData.country = item.name
              break
            case 2: // Area level (e.g., "Country/Area/")
              searchResultsData.area = item.name
              break
            case 3: // Region level (e.g., "Country/Area/Region/")
              searchResultsData.region = item.name
              break
            case 4: // City level (e.g., "Country/Area/Region/City/")
            case 5: // Some cities might have extra slashes
              searchResultsData.city = item.name
              break
          }
        })
      }

      const filters = searchStore.context.filters
      if (
        filters?.dates &&
        typeof filters.dates === 'object' &&
        filters.dates.arrive &&
        filters.dates.depart
      ) {
        searchResultsData.checkin_date = filters.dates.arrive
        searchResultsData.checkout_date = filters.dates.depart
      }

      const party = filters?.party
      if (party) {
        // Only include adults if it's explicitly in the URL (not the default value of 2)
        if (typeof party.adults === 'number' && route.query.adults) {
          searchResultsData.num_adults = party.adults
        }
        if (Array.isArray(party.childAges)) {
          searchResultsData.num_children = party.childAges.length
        }
      }

      const gtmData = {
        event: 'search_results_loaded', // Using a generic event name, can be customized
        'search-results': searchResultsData,
      }
      $gtm.push(gtmData)
      logger('SearchMain.GTM.search-results', gtmData)
    }
  }
}

onMounted(() => {
  logger('SearchMain.onMounted', process.client, route.fullPath)
  logAsyncComponentLifecycle('SearchMain', 'Component mounted', {
    route: route.fullPath,
    showMap: searchStore.showMap,
    resultsCount: campsites.value.length,
  })

  // Removed category-facet validation - validation should only happen when removing facet lozenges
  const lang = (route.params.lang as string) || 'en-gb'

  const elements = document.querySelectorAll('[data-lang]')
  elements.forEach((element) => {
    if (element.getAttribute('data-lang') !== lang) {
      console.error(
        `Unexpected lang attribute: ${element.getAttribute(
          'data-lang',
        )}. Element: ${element.tagName}, Class: ${element.className}, Text: ${
          element.textContent
        }`,
      )
    }
  })

  // Initial GTM push for search results
  pushGtmSearchResultsData(campsites.value)
})

const firstResultImgSrcSet = computed(() => {
  const img = searchStore.context.results?.campsites?.[0]?.primaryPhoto
  if (!img) {
    return
  }
  return transformImageSrcSet(`
    /_cfi/cdn-cgi/image/format=auto,fit=cover,quality=80,w=360,h=270${img.url.masterImage} 360w,
    /_cfi/cdn-cgi/image/format=auto,fit=cover,quality=30,w=720,h=540${img.url.masterImage} 840w,
    /_cfi/cdn-cgi/image/format=auto,fit=cover,quality=30,w=1080,h=810${img.url.masterImage} 1080w
  `)
})

const visibleCampsite: Ref<{ slug: string; point: string | null } | null> =
  ref(null)
const headerIsVisible = ref(true)
const scrolledDown = ref(false)
const asyncComponentKeys = ref<Record<string, string>>({})

// Helper functions for async component keys with debug logging
const getAsyncComponentKey = (componentName: string) => {
  const key =
    asyncComponentKeys.value[componentName] ||
    `${route.fullPath.split('#')[0]}-${componentName}`
  logAsyncComponentLifecycle(componentName, 'Getting component key', { key })
  return key
}
const setAsyncComponentKey = (componentName: string) => {
  const key = `${route.fullPath.split('#')[0]}-${componentName}`
  asyncComponentKeys.value[componentName] = key
  logAsyncComponentLifecycle(componentName, 'Component mounted in template', {
    key,
    route: route.fullPath,
  })
}

const faqList = computed(() => {
  return searchStore.context.results?.faq
})

const cityPoint = computed((): SimpleGeoPoint | undefined => {
  return searchStore.context.results?.point || undefined
})

const heading = computed((): string | undefined => {
  return searchStore.context.results?.heading
})

const title = computed((): string | undefined => {
  return searchStore.context.results?.title
})

const summaryProps = computed(() => {
  const results = searchStore.context.results
  return results
    ? {
        bookableCount: results.bookableCount || 0,
        campsitesCount: results.count || 0,
        hierarchyName: results.hierarchyName || undefined,
        distance: results.within || undefined,
        categoryName: results.category?.name,
        isUserLocationSearch:
          results.within && results.point && !results.hierarchyName,
        isAvailabilitySearch: searchStore.isDated,
      }
    : undefined
})

const hierarchyTrail = computed((): HierarchyTrail[] | undefined => {
  return searchStore.context.results?.hierarchyTrail
})

const currenthierarchyItem = computed((): HierarchyTrail | undefined => {
  const results = searchStore.context.results
  const trail = hierarchyTrail.value
  if (!trail || trail.length === 0 || !results?.hierarchyName) {
    return undefined
  }
  return {
    name: results.hierarchyName,
    path: trail[trail.length - 1].path,
  }
})

const sortOption = computed(() => {
  return searchStore.context.results?.sortOption
})

function headerVisibilityChanged(isVisible: boolean) {
  // Guard: Prevent execution if component is unmounting
  if (!isMountedRef.value) {
    return
  }
  headerIsVisible.value = isVisible
}

const isIntentionalPointSearch = computed(() => {
  if (!searchStore.searchFilters) {
    return false
  }
  const isPointSearchTypeAndFilters = isPointSearch(searchStore.searchFilters)
  const isBoundedSearch = !!searchStore.searchFilters?.bounds
  return isPointSearchTypeAndFilters && !isBoundedSearch
})

function scrolledResultsDown(state: boolean) {
  // Guard: Prevent execution if component is unmounting
  if (!isMountedRef.value) {
    return
  }
  scrolledDown.value = state
}

const pagePart = computed(() => {
  return searchStore.context.results?.pagePart
})

const campsites = computed(() => {
  return searchStore.context.results?.campsites || []
})

// Watch for changes in campsites to push GTM data
watch(
  campsites,
  (newCampsites, oldCampsites) => {
    // Guard: Prevent execution before component is mounted
    if (!isMountedRef.value) {
      return
    }

    // Avoid pushing if the new and old values are the same (e.g. on initial load if immediate:true was used)
    if (JSON.stringify(newCampsites) !== JSON.stringify(oldCampsites)) {
      logger(
        'SearchMain.watch.campsites: Results changed, attempting GTM push.',
        { newCount: newCampsites.length, oldCount: oldCampsites?.length },
      )
      pushGtmSearchResultsData(newCampsites)
    }
  },
  { deep: true },
) // deep watch might be intensive but ensures changes within objects are caught.

const paginationData = computed((): PaginationData | undefined => {
  return searchStore.context.results?.pagination || undefined
})

const states = computed(() => {
  return searchStore.states
})

watch(states, async (newStates, oldStates) => {
  // Guard: Prevent execution before component is mounted
  if (!isMountedRef.value) {
    return
  }

  // Debug logging: track state changes
  logAsyncComponentLifecycle('SearchMain', 'States changed', {
    oldStates,
    newStates,
    route: route.fullPath,
  })

  if (newStates.includes('results')) {
    await nextTick(() => {
      // Add defensive check to prevent scrolling during component transitions
      const scrollTarget = document.querySelector(
        '[data-new-results-scroll-target]',
      )
      if (!scrollTarget) {
        return
      }
      // Additional check: verify the element is connected to the DOM
      if (!scrollTarget.isConnected) {
        return
      }
      $puScrollTo('[data-new-results-scroll-target]', { duration: 300 })
    })
  }
})

const count = computed(() => {
  const count =
    searchStore.context.results?.fullBookableCount === undefined
      ? searchStore.context.results?.count
      : searchStore.context.results?.fullBookableCount
  return count || 0
})

// show low result panel if we have results and less than 6 results
const shouldShowLowResultsPanel = computed(
  () => searchStore.states.includes('results') && count.value < 6,
)

const isCountyOrCity = computed(
  () => hierarchyTrail.value && hierarchyTrail.value.length >= 3,
)

const useSeoPagination = computed(() => {
  if (route.name !== 'glamping-search') {
    return false
  }
  if (
    searchStore?.searchFilters?.facets?.length === undefined ||
    searchStore.searchFilters.facets.length > 1
  ) {
    return false
  }
  if (!hierarchyTrail.value) {
    return true
  }
  if (hierarchyTrail.value.length <= 1) {
    return true
  }
  if (
    hierarchyTrail.value.length === 2 &&
    !searchStore.searchFilters?.facets.length
  ) {
    return true
  }
  return false
})

const shouldShowNotifyMe = computed(
  () =>
    shouldShowLowResultsPanel.value &&
    searchStore.searchFilters?.dates &&
    (!searchStore.searchFilters?.hierarchyPath ||
      (!!searchStore.searchFilters?.hierarchyPath && isCountyOrCity.value)),
)

const firstResult = computed(() => {
  const campsites = searchStore.context.results?.campsites
  if (!campsites || !campsites.length) {
    return undefined
  }
  return campsites[0]
})

function showMapEvent(event: boolean): void {
  // Guard: Prevent execution if component is unmounting
  if (!isMountedRef.value) {
    return
  }
  logger('SearchMain.showMapEvent', event)
  searchStore.toggleMap(event)
}

function mapMarkerClicked(marker: { slug: string }): void {
  // Guard: Prevent execution if component is unmounting
  if (!isMountedRef.value) {
    return
  }
  const activeCard = markActiveCard(marker.slug)
  if (activeCard) {
    void nextTick(() => {
      // Additional check inside nextTick
      if (!isMountedRef.value) {
        return
      }
      $puScrollTo(`li[data-slug="${marker.slug}"]`, {
        duration: 300,
        offset: -200,
      })
    })
  }
}

const debouncedMapMarkerClicked = debounce(
  (event) => mapMarkerClicked(event),
  300,
)

function markActiveCard(slug: string): HTMLElement | null {
  // Guard: Prevent execution if component is unmounting
  if (!isMountedRef.value) {
    return null
  }

  document.querySelectorAll('.search-card.map-marker-opened').forEach((li) => {
    li.classList.remove('map-marker-opened')
  })
  const csDataAttribute = `li[data-slug="${slug}"]`
  const activeCard = document.querySelector(csDataAttribute) as HTMLElement
  if (activeCard) {
    activeCard.classList.add('map-marker-opened')
    return activeCard
  }
  return null
}

function onVisibleCampsiteChange($event: {
  slug: string
  point: string | null
}) {
  // Guard: Prevent execution if component is unmounting
  if (!isMountedRef.value) {
    return
  }
  logger('SearchMain.onVisibleCampsiteChange', $event)
  visibleCampsite.value = $event
  markActiveCard($event.slug)
}

/*
  SEO Meta Data
*/

const runtimeConfig = useRuntimeConfig()
// nuxtApp and $isDesktop are already destructured from useNuxtApp() at the top

const imageObjects = computed(() => {
  const scriptData = [] as Array<{
    type: string
    body: boolean
    json: Record<string, unknown>
  }>
  if (facetMetaData.value?.og_image) {
    scriptData.push({
      type: 'application/ld+json',
      body: true,
      json: {
        '@context': 'https://schema.org',
        '@type': 'ImageObject',
        description: facetMetaData.value.name,
        contentUrl: `https://${runtimeConfig.public.envUrl}${facetMetaData.value.og_image}`,
      },
    })
  }
  if (searchStore?.context?.results?.campsites?.length) {
    const numberOfResults = searchStore.context.results.campsites.length
    for (let i = 0; i < numberOfResults; i++) {
      const campsite = searchStore.context.results?.campsites[i]
      if (campsite?.bookable) {
        scriptData.push(schemaImageObject(i))
      }
    }
  }
  return scriptData
})

function schemaImageObject(index: number): {
  type: string
  body: boolean
  json: Record<string, unknown>
} {
  const includeCaption = $i18n.locale.split('-')[0] === 'en'
  const campsite = searchStore.context.results?.campsites?.[index]
  const json = {
    '@context': 'https://schema.org',
    '@type': 'ImageObject',
    name: campsite?.name,
    contentLocation: `${campsite?.name}, ${campsite?.hierarchyStringShort}`,
    contentUrl: `https://${runtimeConfig.public.envUrl}/_cfi/cdn-cgi/image/format=auto,fit=cover,quality=30,w=1080,h=810${campsite?.primaryPhoto?.url.masterImage}`,
    description: includeCaption ? campsite?.primaryPhoto?.caption : undefined,
  }
  return {
    type: 'application/ld+json',
    body: true,
    json,
  }
}

const ogTitle = computed(() => {
  return {
    hid: 'og:title',
    property: 'og:title',
    content: title.value || '',
  }
})

const ogDescription = computed(() => {
  return {
    hid: 'description',
    name: 'description',
    property: 'og:description',
    content: searchStore.context.results?.description || '',
    'data-brainlabs-count': count.value,
    'data-brainlabs-price': searchStore.context.results?.leadPrice
      ? `${searchStore.context.results?.leadPrice[0].currency} ${searchStore.context.results?.leadPrice[0].amount}`
      : '',
  }
})

const ogUrl = computed(() => {
  return {
    hid: 'og:url',
    property: 'og:url',
    content: `https://${runtimeConfig.public.envUrl}${route.path}`,
  }
})

const ogImage = computed(() => {
  if (firstResult.value?.primaryPhoto?.url?.masterImage) {
    return {
      hid: 'og:image',
      property: 'og:image',
      content: `https://${runtimeConfig.public.envUrl}${firstResult.value.primaryPhoto.url.masterImage}`,
    }
  }
  return null
})

const ogImageCaption = computed(() => {
  if (firstResult.value?.primaryPhoto && $i18n.locale.split('-')[0] === 'en') {
    return {
      hid: 'og:image:alt',
      property: 'og:image:alt',
      content: firstResult.value.primaryPhoto.caption || '',
    }
  }
  return null
})

const ogImageWidth = {
  hid: 'og:image:width',
  property: 'og:image:width',
  content: '3200', // Changed to string
}

const ogImageHeight = {
  hid: 'og:image:height',
  property: 'og:image:height',
  content: '2400', // Changed to string
}

const facetOgImageWidth = {
  hid: 'og:image:width',
  property: 'og:image:width',
  content: '1800', // Changed to string
}

const facetOgImageHeight = {
  hid: 'og:image:height',
  property: 'og:image:height',
  content: '1350', // Changed to string
}

const isCityLevel = computed(
  () => hierarchyTrail.value && hierarchyTrail.value.length >= 4,
)

const facetMetaData = computed(() => {
  if (
    searchStore.searchFilters?.facets &&
    searchStore.searchFilters.facets?.length === 1 &&
    searchStore.context.results?.facetModels
  ) {
    const facet = searchStore.searchFilters.facets[0]
    if (facet) {
      return searchStore.context.results?.facetModels[facet] || null
    }
  }
  return null
})

const facetOgImage = computed(() => {
  return {
    hid: 'og:image',
    property: 'og:image',
    content: `https://${runtimeConfig.public.envUrl}${facetMetaData.value?.og_image}`,
  }
})

const facetOgImageCaption = computed(() => {
  return {
    hid: 'og:image:alt',
    property: 'og:image:alt',
    content: facetMetaData.value?.name || '',
  }
})

// Custom title for lat/lng searches (includes price and optional facet)
const customTitle = computed(() => {
  const hasLatLng = route.query.lat && route.query.lng
  const placename = route.query.placename
  const facetSlug = route.query.facet
  const results = searchStore.context.results
  const categoryName = results?.category?.name
  const leadPrice = results?.leadPrice

  // Only override if we have lat/lng coordinates and valid price data
  if (
    !hasLatLng ||
    !leadPrice ||
    !Array.isArray(leadPrice) ||
    leadPrice.length !== 2 ||
    !leadPrice[0]?.amount ||
    !leadPrice[1] ||
    leadPrice[1] <= 0
  ) {
    return null
  }

  // Look up facet display name if facet parameter exists
  let facetName: string | null = null
  if (facetSlug && typeof facetSlug === 'string' && results?.facetModels) {
    const facet = results.facetModels[facetSlug]
    facetName = facet?.name ?? null
  }

  // Format price as per-night rate using puCurrency formatter for proper i18n
  const priceObj = leadPrice[0]
  const nights = leadPrice[1]
  const pricePerNight = priceObj.amount / nights
  const formattedPrice = puCurrency(
    pricePerNight,
    $i18n.locale,
    `currency:${priceObj.currency} minimumFractionDigits:0 maximumFractionDigits:0`,
  )
  const priceFormatted = `${formattedPrice}/nt`

  // Check if this is a user location search
  const isUserLocationSearch =
    results?.within && results?.point && !results?.hierarchyName

  // Case 1: "Near me" search (user's current location)
  if (!placename && isUserLocationSearch) {
    // With facet: "{facet} {category} from {price} near me" or "{facet} campsites from {price} near me"
    if (facetName) {
      if (categoryName) {
        return $t('{facetName} {categoryName} from {price} near me', {
          facetName,
          categoryName,
          price: priceFormatted,
        })
      }
      return $t('{facetName} campsites from {price} near me', {
        facetName,
        price: priceFormatted,
      })
    }

    // Without facet: Use existing translations
    if (categoryName) {
      return $t('{categoryName} from {price} near me', {
        categoryName,
        price: priceFormatted,
      })
    }
    return $t('Campsites from {price} near me', {
      price: priceFormatted,
    })
  }

  // Case 2: Named location search
  if (!placename) {
    return null // No placename and not a user location search
  }

  // Decode placename
  let decodedPlacename: string
  try {
    decodedPlacename = decodeURIComponent(String(placename).replace(/\+/g, ' '))
  } catch {
    return null
  }

  if (!decodedPlacename) {
    return null
  }

  // With facet: "{facet} {category} from {price} near {placename}" or "{facet} campsites from {price} near {placename}"
  if (facetName) {
    if (categoryName) {
      return $t('{facetName} {categoryName} from {price} near {placename}', {
        facetName,
        categoryName,
        price: priceFormatted,
        placename: decodedPlacename,
      })
    }
    return $t('{facetName} campsites from {price} near {placename}', {
      facetName,
      price: priceFormatted,
      placename: decodedPlacename,
    })
  }

  // Without facet: Use existing translations
  if (categoryName) {
    return $t('{categoryName} from {price} near {placename}', {
      categoryName,
      price: priceFormatted,
      placename: decodedPlacename,
    })
  }

  return $t('Campsites from {price} near {placename}', {
    price: priceFormatted,
    placename: decodedPlacename,
  })
})

const pageTitle = computed(() => {
  // Use custom title if available
  const _title = customTitle.value || title.value || 'Pitchup.com search'
  if (route.query.page && Number(route.query.page) > 1) {
    return _title + ' | ' + route.query.page
  }
  return _title
})

// Defensive function to clean meta objects and ensure all properties are strings
const cleanMetaObject = (
  metaObj: Record<string, unknown> | null,
): Record<string, unknown> | null => {
  if (!metaObj || typeof metaObj !== 'object') return metaObj

  const cleaned: Record<string, unknown> = {}

  // Only include properties that have valid string values (or numbers for content)
  if (metaObj.hid && typeof metaObj.hid === 'string' && metaObj.hid.trim()) {
    cleaned.hid = metaObj.hid
  }

  if (metaObj.name && typeof metaObj.name === 'string' && metaObj.name.trim()) {
    cleaned.name = metaObj.name
  }

  if (
    metaObj.property &&
    typeof metaObj.property === 'string' &&
    metaObj.property.trim()
  ) {
    cleaned.property = metaObj.property
  }

  if (metaObj.content !== undefined && metaObj.content !== null) {
    if (
      typeof metaObj.content === 'string' ||
      typeof metaObj.content === 'number'
    ) {
      cleaned.content = metaObj.content
    } else {
      cleaned.content = String(metaObj.content)
    }
  }

  // Preserve data attributes
  Object.keys(metaObj).forEach((key) => {
    if (key.startsWith('data-') && metaObj[key] !== undefined) {
      cleaned[key] = metaObj[key]
    }
  })

  return cleaned
}

const head = computed(() => {
  const query = { ...route.query }
  delete query.page
  const hasQueryParams = Object.keys(query).length
  const meta: Array<{
    name?: string
    content: string | number // Allow number for data-brainlabs-count
    hid: string
    property?: string
    'data-brainlabs-count'?: number
    'data-brainlabs-price'?: string
  }> = [ogTitle.value, ogDescription.value, ogUrl.value]
  if (hasQueryParams) {
    meta.push({
      name: 'robots',
      content: 'noindex',
      hid: 'robots',
    })
  }
  const headData = {
    title: pageTitle.value,
    meta,
    link: [] as Array<{
      rel: string
      href?: string
      hid?: string
      as?: string
      imagesrcset?: string
      imagesizes?: string
      fetchpriority?: string // Re-added fetchpriority as it was in original, ensure it's valid or handle appropriately
    }>,
    bodyAttrs: {
      ...($isDesktop ? { 'data-desktop-search': 'true' } : {}),
    },
    script: [
      {
        type: 'application/ld+json',
        body: true,
        innerHTML: JSON.stringify(SCHEMA_ORGANIZATION),
        hid: 'schema-organization',
      },
      ...imageObjects.value,
    ],
  }
  if (
    paginationData.value &&
    paginationData.value?.currentPage &&
    paginationData.value?.currentPage > 1
  ) {
    headData.link.push({
      hid: 'i18n-can',
      rel: 'canonical',
      href: `https://${runtimeConfig.public.envUrl}${route.path}?page=${paginationData.value?.currentPage}`,
    })
  }
  if (!$isDesktop && firstResultImgSrcSet.value) {
    logger('SearchMain.preloadImage', firstResultImgSrcSet.value)
    headData.link.push({
      rel: 'preload',
      as: 'image',
      imagesrcset: firstResultImgSrcSet.value,
      imagesizes: '100vw',
      fetchpriority: 'high', // Restored fetchpriority
    })
  }
  const facetOgImageMeta = [
    cleanMetaObject(facetOgImage.value),
    cleanMetaObject(facetOgImageCaption.value),
    cleanMetaObject(facetOgImageWidth),
    cleanMetaObject(facetOgImageHeight),
  ].filter(
    (
      item,
    ): item is {
      name?: string
      content: string | number
      hid: string
      property?: string
      'data-brainlabs-count'?: number
      'data-brainlabs-price'?: string
    } => item !== null && 'hid' in item && 'content' in item,
  )
  const ogImageMeta = [
    cleanMetaObject(ogImage.value),
    cleanMetaObject(ogImageCaption.value),
    cleanMetaObject(ogImageWidth),
    cleanMetaObject(ogImageHeight),
  ].filter(
    (
      item,
    ): item is {
      name?: string
      content: string | number
      hid: string
      property?: string
      'data-brainlabs-count'?: number
      'data-brainlabs-price'?: string
    } => item !== null && 'hid' in item && 'content' in item,
  )
  if (facetMetaData.value?.og_image) {
    headData.meta = [...headData.meta, ...facetOgImageMeta]
  } else {
    headData.meta = [...headData.meta, ...ogImageMeta]
  }
  return headData
})
</script>

<style>
[data-desktop-search='true'] .app-header {
  max-width: 100%;
}
</style>

<style scoped>
.search-main {
  min-height: 72vh;
}

.header-wrapper {
  background-color: var(--colour-page-background);
  height: 26vw;
  position: absolute;
  top: 0;
  z-index: 3;
}

.search-results-column {
  background-color: var(--colour-page-background);
  padding-top: 26vw;
  position: relative;
  z-index: 10;
}

.search-results-column :deep(article) {
  background-color: white;
}

.search-error {
  background-color: var(--colour-page-background);
  color: var(--colour-mid-blue);
  font-family: monospace;
  font-size: var(--small);
  padding: 1em 1.4em;
  word-break: break-word;
}

.pinned {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
}

.navbar {
  position: relative;
  z-index: 2;
}

.summary-bar {
  transition: transform 0.25s ease-in-out;
  transform: none;
  position: relative;
  z-index: 1;
  overflow: visible;
}

.navbar:after,
.summary-bar:after {
  box-shadow: 0 0.075em 0.15em rgba(0, 0, 0, 0.4);
  content: '';
  height: 100%;
  left: 0;
  opacity: 0;
  position: absolute;
  top: 0;
  transition: opacity 0.2s ease-out;
  width: 100%;
  z-index: -1;
}

.pinned.showing-summary.down .navbar:after,
.pinned.showing-summary .summary-bar:after,
.pinned:not(.showing-summary) .navbar:after {
  opacity: 1;
}

.pinned.down .summary-bar {
  transform: translateY(-14vw);
}

.map-placeholder {
  display: none;
}

.search-breadcrumbs {
  padding: 2em 1.4em;
}

.search-breadcrumbs :deep(.breadcrumbs-generator a:last-of-type) {
  margin-right: 0;
}

/* mobile landscape */
@media screen and (min-width: 320px) and (max-width: 998px) and (orientation: landscape) {
  .search-results-column {
    padding-top: 5em;
  }
  .header-wrapper {
    height: 5em;
  }
}

/* desktop & tablet */
.search-main.desktop {
  width: 100%;
  display: flex;
  flex-direction: column;
}

.desktop .search-breadcrumbs {
  background: white;
  padding: 0 var(--page-width-padding);
  position: sticky;
  top: 0;
  z-index: 3;
}

.desktop .search-results-section {
  display: flex;
  min-height: calc(100dvh - 88px);
}

.desktop .search-results-section :deep(.horizontal-search-bar .modal) {
  max-height: calc(100dvh - 3em);
}

.desktop .search-results-column {
  width: 100%;
  padding: 0;
  z-index: initial;
  display: flex;
  flex-direction: column;
  background: white;
}

.desktop .map-placeholder {
  width: 40%;
  background: #7abcec;
  position: sticky;
  top: 28px;
  height: calc(100dvh - 28px);
}

.desktop .map-placeholder :deep(.pu-map.search-results-map) {
  height: 100% !important;
}

.desktop .show-map .search-results-column {
  width: 60%;
}

.desktop .show-map .map-placeholder {
  display: block;
}

.desktop .show-map :deep(.search-card.map-marker-opened .campsite-card) {
  box-shadow: 0 0 0 0.33em black;
  transition: box-shadow 150ms ease-out;
}

.desktop .search-bar-desktop {
  align-items: center;
  background: var(--colour-page-background-light);
  display: flex;
  flex-direction: column-reverse;
  justify-content: flex-start;
  min-height: 4.5em;
  order: -1;
  position: sticky;
  top: 28px;
  z-index: 10;
}

.desktop .search-bar-desktop :deep(.guests-cell.children),
.desktop .search-bar-desktop :deep(.guests-cell.dogs) {
  display: none;
}

.desktop .search-bar-desktop :deep(.guests-cell.children.has-children),
.desktop .search-bar-desktop :deep(.has-dogs .dogs) {
  display: flex;
}

.desktop .search-bar-desktop :deep(.selected-categories .disabled) {
  display: none;
}

.desktop .search-bar-desktop :deep(.selected-categories) {
  margin: auto;
  gap: 0.4em;
}

.desktop .search-bar-desktop :deep(.selected-categories img) {
  margin: 0;
}

.desktop
  .search-bar-desktop
  :deep(.horizontal-search-bar .accommodation-wizard .summary),
.desktop .search-bar-desktop :deep(.horizontal-search-bar .summary) {
  min-height: 2.6em;
  padding: 0.5em 1em;
}

.desktop .search-bar-desktop :deep(.horizontal-search-bar .autosuggest-input) {
  min-height: 2.6em;
  font-size: 1em;
}

.desktop .search-bar-desktop :deep(.horizontal-search-bar) {
  padding: 1em 1em 1em var(--page-width-padding);
}

.desktop .search-bar-desktop :deep(.horizontal-search-bar .summary),
.desktop .search-bar-desktop :deep(.horizontal-search-bar .summary p) {
  white-space: nowrap;
}

.desktop .search-bar-desktop :deep(.horizontal-search-bar .search-magnify-btn) {
  width: 2.5em;
  height: 2.5em;
}

.desktop .search-bar-desktop :deep(.search-submit-btn) {
  display: none;
}

.desktop .search-actions,
.desktop .search-summary-bar.summary-bar,
.desktop .header-wrapper {
  height: auto;
  width: auto;
  border: none;
}

.desktop .header-wrapper {
  order: -1;
  position: relative;
  top: auto;
  transition: box-shadow 0.35s ease-in-out;
  width: 100%;
}

.desktop .header-wrapper.pinned {
  box-shadow: 0 5px 3px -3px #00000030;
}

.desktop .showing-summary {
  display: flex;
  flex-direction: column-reverse;
}

.desktop .navbar:after,
.desktop .summary-bar:after {
  content: none;
}

.desktop .seo-part {
  order: -1;
}

.desktop .facet-search-image {
  align-self: flex-start;
  margin: 1.4em;
  border-radius: 0.5em;
}

.search-main.desktop :deep(.low-results .countries-list a) {
  font-size: inherit;
}

.search-main.desktop :deep(.dates-wizard.no-dates .summary) {
  border: 3px solid var(--colour-orange);
}

/* desktop & tablet SMALL */
@media only screen and (max-width: 1279px) {
  .desktop .map-placeholder {
    width: 50%;
  }

  .desktop .show-map .search-results-column {
    width: 50%;
  }
}
</style>
